/**
 * Typed API client for the Terramantle Worker.
 * All requests include the Auth0 access token as a Bearer header.
 *
 * Functions are added here as routes are implemented in Phases 2–3.
 */

let getAccessToken: (() => Promise<string>) | null = null

export function setTokenProvider(fn: () => Promise<string>): void {
  getAccessToken = fn
}

async function authHeader(): Promise<Record<string, string>> {
  if (!getAccessToken) return {}
  const token = await getAccessToken()
  return { Authorization: `Bearer ${token}` }
}

export async function authorizationHeader(): Promise<string> {
  if (!getAccessToken) return ''
  const token = await getAccessToken()
  return `Bearer ${token}`
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    ...(await authHeader()),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
  }

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'unknown' }))
    throw Object.assign(new Error((err as { error: string }).error), {
      status: res.status,
      body: err,
    })
  }

  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
}
